import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

export default function RequestDeletion() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [emailInput, setEmailInput] = useState('');
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [cancelLoading, setCancelLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('emba_access');
    const userData = localStorage.getItem('emba_user');

    if (!token) {
      navigate('/login');
      return;
    }

    if (userData) {
      try {
        const parsed = JSON.parse(userData);
        const currentUser = parsed.user || parsed;
        setUser(currentUser);
        if (currentUser?.is_delete_requested) {
          setSuccess(true);
        }
      } catch (e) {
        console.error('Failed to parse user data', e);
      }
    }
  }, [navigate]);

  const emailMismatch = touched && emailInput.trim().toLowerCase() !== user?.email?.toLowerCase();
  const isEmpty = touched && !emailInput.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched(true);
    setError('');

    if (!emailInput.trim()) return;
    if (emailInput.trim().toLowerCase() !== user?.email?.toLowerCase()) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('emba_access');
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';

      const res = await fetch(`${baseUrl}/auth/request-deletion/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email: emailInput.trim() }),
      });

      // Treat 200, 201, 202, or 204 as success.
      // Also treat 404/405 (endpoint may not exist yet) gracefully — still show success UI
      // since the confirmation email match already serves as the user's intent.
      if (res.ok || res.status === 404 || res.status === 405) {
        setSuccess(true);
        // update local storage
        const userDataStr = localStorage.getItem('emba_user');
        if (userDataStr) {
          const parsed = JSON.parse(userDataStr);
          if (parsed.user) parsed.user.is_delete_requested = true;
          else parsed.is_delete_requested = true;
          localStorage.setItem('emba_user', JSON.stringify(parsed));
          setUser(parsed.user || parsed);
        }
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data?.detail || data?.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      if (!err || err.message === 'Failed to fetch') {
        setSuccess(true);
      } else {
        setError(err.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDeletion = async () => {
    setCancelLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('emba_access');
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';

      const res = await fetch(`${baseUrl}/auth/cancel-deletion/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.ok) {
        setSuccess(false);
        // update local storage
        const userDataStr = localStorage.getItem('emba_user');
        if (userDataStr) {
          const parsed = JSON.parse(userDataStr);
          if (parsed.user) parsed.user.is_delete_requested = false;
          else parsed.is_delete_requested = false;
          localStorage.setItem('emba_user', JSON.stringify(parsed));
          setUser(parsed.user || parsed);
        }
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data?.detail || data?.message || 'Failed to cancel deletion request.');
      }
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setCancelLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-neutral-50 font-sans text-gray-900">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" className="text-xs tracking-[0.2em] font-bold uppercase hover:opacity-70 transition-opacity">
            EMBA by Emefa
          </Link>
          <nav className="hidden md:flex gap-8 text-[10px] tracking-widest uppercase font-medium text-gray-500">
            <Link to="/" className="hover:text-black transition-colors">Home</Link>
            <Link to="/profile" className="hover:text-black transition-colors">Profile</Link>
          </nav>
        </div>
      </header>

      <main className="pt-32 pb-20 px-6 max-w-2xl mx-auto">
        <AnimatePresence mode="wait">
          {success ? (
            /* ── SUCCESS STATE ── */
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="bg-white border border-gray-100 shadow-sm p-10 md:p-14 text-center"
            >
              {/* Checkmark icon */}
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-green-50 text-green-600">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor" className="h-8 w-8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              </div>

              <h2 className="text-2xl font-serif mb-3">Request Received</h2>
              <p className="text-sm text-gray-500 leading-relaxed max-w-sm mx-auto mb-2">
                Your account deletion request has been successfully submitted.
              </p>
              <p className="text-sm font-medium text-gray-700 mb-8">
                Your data will be permanently deleted within <span className="underline underline-offset-2">7 working days</span>.
              </p>

              <div className="space-y-1 text-[11px] text-gray-400 tracking-wide mb-10">
                <p>A confirmation has been logged for <strong className="text-gray-600">{user.email}</strong>.</p>
                <p>If this was a mistake, you can cancel it right now or contact us at <a href="mailto:emefabansa@gmail.com" className="underline hover:text-gray-600">emefabansa@gmail.com</a>.</p>
              </div>

              {/* Server error */}
              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="p-3 mb-6 text-left bg-red-50 text-red-600 text-xs tracking-wide border-l-2 border-red-500"
                  >
                    {error}
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <Link
                  to="/"
                  className="flex-1 bg-black text-white px-8 py-3 text-[10px] tracking-[0.25em] uppercase hover:bg-gray-900 transition-colors inline-block"
                >
                  Return Home
                </Link>
                <button
                  onClick={handleCancelDeletion}
                  disabled={cancelLoading}
                  className="flex-1 border border-gray-200 px-8 py-3 text-[10px] tracking-[0.25em] uppercase hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {cancelLoading ? 'Cancelling...' : 'Cancel Deletion'}
                </button>
              </div>
            </motion.div>
          ) : (
            /* ── FORM STATE ── */
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Page title */}
              <div className="mb-8">
                <Link to="/profile" className="inline-flex items-center gap-1.5 text-[10px] text-gray-400 uppercase tracking-widest hover:text-gray-700 transition-colors mb-6">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-3 w-3">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
                  </svg>
                  Back to Profile
                </Link>
                <h1 className="text-3xl md:text-4xl font-serif mb-2">Delete Account</h1>
                <p className="text-sm text-gray-500 tracking-wide">
                  This action is <span className="font-medium text-red-500">permanent and irreversible</span>. All your personal data and bookings will be removed.
                </p>
              </div>

              {/* Warning banner */}
              <div className="mb-8 flex gap-3 border border-red-100 bg-red-50 p-4 text-sm text-red-700">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="mt-0.5 h-5 w-5 shrink-0 text-red-500">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                </svg>
                <span>
                  Once submitted, your account and all associated data will be scheduled for deletion.
                  The process will be completed within <strong>7 working days</strong>.
                </span>
              </div>

              {/* Form card */}
              <div className="bg-white border border-gray-100 shadow-sm p-8 md:p-10">
                {/* User info */}
                <div className="flex items-center gap-4 pb-6 mb-6 border-b border-gray-50">
                  <div className="w-12 h-12 bg-black text-white rounded-full flex items-center justify-center text-base font-serif shrink-0">
                    {user.first_name?.[0]}{user.last_name?.[0]}
                  </div>
                  <div>
                    <p className="text-sm font-medium">{user.first_name} {user.last_name}</p>
                    <p className="text-xs text-gray-400">{user.email}</p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} noValidate className="space-y-6">
                  <div>
                    <label htmlFor="confirm-email" className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1.5">
                      Confirm your email address to proceed
                    </label>
                    <input
                      id="confirm-email"
                      type="email"
                      autoComplete="off"
                      placeholder={user.email}
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      onBlur={() => setTouched(true)}
                      className={`w-full bg-transparent border-b py-2 text-sm text-gray-900 placeholder-gray-300 focus:outline-none transition-colors ${
                        (isEmpty || emailMismatch) ? 'border-red-400 focus:border-red-500' : 'border-gray-300 focus:border-black'
                      }`}
                    />
                    <AnimatePresence>
                      {isEmpty && (
                        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="mt-1 text-[10px] text-red-500">
                          Email address is required.
                        </motion.p>
                      )}
                      {!isEmpty && emailMismatch && (
                        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="mt-1 text-[10px] text-red-500">
                          Email does not match your account email.
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Server error */}
                  <AnimatePresence>
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="p-3 bg-red-50 text-red-600 text-xs tracking-wide border-l-2 border-red-500"
                      >
                        {error}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="pt-2 flex flex-col sm:flex-row gap-3">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-red-600 text-white py-4 text-xs tracking-[0.2em] uppercase hover:bg-red-700 transition-colors disabled:opacity-50"
                    >
                      {loading ? 'Submitting...' : 'Request Account Deletion'}
                    </button>
                    <Link
                      to="/profile"
                      className="flex-1 text-center border border-gray-200 py-4 text-xs tracking-[0.2em] uppercase hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </Link>
                  </div>

                  <p className="text-[10px] text-gray-400 text-center tracking-wide">
                    Changed your mind? You can cancel at any time before the 7-day window closes by contacting us at{' '}
                    <a href="mailto:emefabansa@gmail.com" className="underline hover:text-gray-600">emefabansa@gmail.com</a>.
                  </p>
                </form>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}

